/** @format */

import chroma from 'chroma-js';

/**
 * @mixin
 * Sharable mixin to use with app theming
 */
export default {
	methods: {
		/**
		 * @public
		 * @description
		 * Setup color contrast
		 * Utility function to determine the correct contrasting foreground
		 * colour for a given background colour
		 *
		 * @param {String} baseColour The base colour to determine contrast for
		 * @param {String} bodyTextColour The body text colour to default to if the button is light and the body text is dark, or vice versa
		 * @param {String} darkColour The dark colour value to test
		 * @param {String} lightColour The dark colour value to test
		 *
		 * @returns {String}
		 */
		getContrastColour(baseColour, bodyTextColour, darkColour, lightColour) {
			let colour, bodyColour;

			if (typeof baseColour === 'string' && baseColour.trim().length > 0) {
				colour = chroma(baseColour.trim());
			} else {
				colour = chroma('#000000');
			}

			if (typeof bodyTextColour === 'string' && bodyTextColour.trim().length > 0) {
				bodyColour = chroma(bodyTextColour.trim());
			} else {
				bodyColour = chroma('#ffffff');
			}

			// If colour is light
			if (colour.get('lab.l') > 75) {
				if (bodyColour.get('lab.l') > 70) {
					return typeof darkColour === 'string' ? darkColour.trim() : '';
				} else {
					return bodyColour.hex();
				}
			}

			// If colour is dark
			// If body colour is light returns body text colour otherwise brighten colour
			if (bodyColour.get('lab.l') > 70) {
				return bodyColour.hex();
			} else {
				return typeof lightColour === 'string' ? lightColour.trim() : '';
			}
		},

		/**
		 * @public
		 * @description
		 * Utility function to darken a given colour by a given percentage amount
		 *
		 * @param {String} baseColour The base colour to change
		 * @param {Number} percentage The percentage amount to darken by
		 *
		 * @returns {String}
		 */
		darkenColour(baseColour, percentage) {
			if (typeof baseColour === 'undefined' || (typeof baseColour === 'string' && baseColour.trim().length === 0)) {
				return '';
			}
			return chroma(baseColour.trim())
				.darken(percentage / 100)
				.hex();
		},

		/**
		 * @public
		 * @description
		 * Utility function to darken a given colour by a given percentage amount
		 *
		 * @param {String} baseColour The base colour to change
		 * @param {Number} percentage The percentage amount to lighten by
		 *
		 * @returns {String}
		 */
		lightenColour(baseColour, percentage) {
			if (typeof baseColour === 'undefined' || (typeof baseColour === 'string' && baseColour.trim().length === 0)) {
				return;
			}
			return chroma(baseColour.trim())
				.brighten(percentage / 100)
				.hex();
		},

		/**
		 * @public
		 * @description
		 * Takes the provided theme settings and merges them with sass file defaults.
		 *
		 * @param {Object} themeSettings
		 */
		mergeThemeColours(themeSettings) {
			try {
				return Object.assign(themeSettings, {
					colors: Object.assign({}, this.$themeDefaultColours(), themeSettings.colors)
				});
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: true,
					errorCode: '000241'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Handles reloading the theme when edited via the config UI.
		 *
		 * @param {Object} newThemeSettings
		 */
		updateTheme(newThemeSettings) {
			try {
				let currentEntity = false;

				// TODO: this.entities will always be empty because we call
				// the updateTheme from cs-app which doesn't have entities anymore
				if (Array.isArray(this.entities)) {
					for (let i = 0, entity; (entity = this.entities[i]); i++) {
						if (entity.active && entity.themeTemplateId === newThemeSettings.id) {
							this.theme = newThemeSettings.themeSettings;
							currentEntity = true;
							break;
						}
					}
				}

				if (currentEntity) {
					this.setupThemeCss();
				}
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: true,
					errorCode: '000229'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Creates a rule in the stylesheet for the theme colour or theme palette colour.
		 * It accepts an unholy amount of variables.
		 *
		 * @param {string} colourKey	generally this is the css class name
		 * @param {string} colourValue	the hex code for this colour
		 * @param {object} styleSheet	the stylesheet to add the rule to
		 * @param {string} bodyFg		the hex code for the body colour
		 * @param {string} dark			the hex code for the dark colour
		 * @param {string} light		the hex code for the light colour
		 */
		generateStylesheetRulesForThemeColour(colourKey, colourValue, styleSheet, bodyFg, dark, light) {
			const bgSelector = `.cs-bg-${colourKey}`,
				fgSelector = `.cs-fg-${colourKey}`,
				bcSelector = `.cs-bc-${colourKey}`,
				scSelector = `.cs-sc-${colourKey}`,
				ocSelector = `.cs-oc-${colourKey}`;

			if (typeof colourValue !== 'undefined' && typeof colourValue === 'string' && colourValue.length !== 0) {
				// standard
				// need to add the second param for insertRule to help IE understand what we're doing
				this.insertRuleIntoStylesheet(styleSheet, `${bgSelector} { background-color: ${colourValue} !important; color: ${this.getContrastColour(colourValue, bodyFg, dark, light)};  }`);

				// a
				this.insertRuleIntoStylesheet(styleSheet, `a${bgSelector}:hover, a${bgSelector}:focus { background-color: ${this.darkenColour(colourValue, 10)} !important; }`);

				// button
				let buttonBodyColour = typeof this.darkenColour(colourValue, 10) !== 'undefined' ? this.darkenColour(colourValue, 10) : bodyFg;
				this.insertRuleIntoStylesheet(styleSheet, `button${bgSelector}:hover, button${bgSelector}:focus { background-color: ${this.getContrastColour(colourValue, buttonBodyColour, this.darkenColour(colourValue, 10), this.lightenColour(colourValue, 10))} !important; }`);
				this.insertRuleIntoStylesheet(
					styleSheet,
					`button${bgSelector}:hover:active, button${bgSelector}:active { background-color: ${this.getContrastColour(colourValue, buttonBodyColour, this.darkenColour(colourValue, 20), this.lightenColour(colourValue, 20))} !important; }`
				);

				// foreground
				this.insertRuleIntoStylesheet(styleSheet, `${fgSelector} { color: ${colourValue} !important; fill: ${colourValue};  }`);

				// border colors
				this.insertRuleIntoStylesheet(styleSheet, `${bcSelector} { border-color: ${colourValue} !important; }`);

				// stroke colors
				this.insertRuleIntoStylesheet(styleSheet, `${scSelector} { stroke: ${colourValue} !important; }`);

				//outline colors
				this.insertRuleIntoStylesheet(styleSheet, `${ocSelector} { outline-color: ${colourValue} !important; }`);

				//accessible border colours
				this.insertRuleIntoStylesheet(styleSheet, `${bgSelector}-accessible:focus { box-shadow: 0 0 0 2px ${this.getContrastColour(colourValue, bodyFg, dark, light)}, 0 0 0 4px ${colourValue}}`);
			}
		},

		// Create a function for setting a variable value
		setCustomCssVariable(colourName, colourValue, cssStyleSheet) {
			cssStyleSheet.style.setProperty(colourName, colourValue);
  		},

		/**
		 * @public
		 * @description
		 * Takes the current theme object and generates the appropriate CSS out of it
		 * for theming purposes
		 */
		setupThemeCss() {
			try {
				if (this.theme) {
					let themeSheet = document.getElementById('themeCSS');
					if (themeSheet) {
						while (themeSheet.sheet.cssRules.length > 0) {
							themeSheet.sheet.deleteRule(0);
						}
					} else {
						themeSheet = document.createElement('style');
						themeSheet.type = 'text/css';
						themeSheet.setAttribute('id', 'themeCSS');

						document.head.appendChild(themeSheet);
					}

					const styleSheet = themeSheet.sheet;
					const customCssStyleSheet = document.querySelector(':root');
					
					// create color rules
					// get our reference colours
					let bodyBg = this.theme.colors && typeof this.theme.colors.bodybackground === 'string' && this.theme.colors.bodybackground.length > 0 ? this.theme.colors.bodybackground : document.body.style.backgroundColor,
						bodyFg = this.theme.colors && typeof this.theme.colors.bodytext === 'string' && this.theme.colors.bodytext.length > 0 ? this.theme.colors.bodytext : document.body.style.color,
						dark = this.theme.colors && typeof this.theme.colors.dark === 'string' && this.theme.colors.dark.length > 0 ? this.theme.colors.dark : '#111',
						light = this.theme.colors && typeof this.theme.colors.light === 'string' && this.theme.colors.light.length > 0 ? this.theme.colors.light : '#fff';

					if (this.theme.colors) {
						for (let colourKey in this.theme.colors) {
							if (Array.isArray(this.theme.colors[colourKey])) {
								for (let p = 0, pLength = this.theme.colors[colourKey].length; p < pLength; p++) {
									const paletteKey = this.theme.colors[colourKey][p]['classSuffix'] || this.theme.colors[colourKey][p]['color'].replace(/[^A-Fa-f0-9#]/g, '').replace(/#/, '\\#');
									// ^ we want to allow the # in the css class if we are using a hex colour, this removes the need to change other code in the system
									// to allow the # in the stylesheet we must escape it
									const colourValue = this.theme.colors[colourKey][p]['color'];
									this.generateStylesheetRulesForThemeColour(paletteKey, colourValue, styleSheet, bodyFg, dark, light);									
								}								
							} else {
								const colourValue = this.theme.colors[colourKey];
								if (typeof colourValue === 'string') {
									let paletteKey = colourValue.replace(/[^A-Fa-f0-9#]/g, '').replace(/#/, '\\#');
									this.generateStylesheetRulesForThemeColour(paletteKey, colourValue, styleSheet, bodyFg, dark, light);
									this.setCustomCssVariable(`--cs-col-${colourKey}`, colourValue, customCssStyleSheet);
									this.setCustomCssVariable(`--cs-col-${colourKey}-contrast`, this.getContrastColour(colourValue, bodyFg, dark, light), customCssStyleSheet);														
								}
								this.generateStylesheetRulesForThemeColour(colourKey, colourValue, styleSheet, bodyFg, dark, light);
							}							
						}
					}

					// create body rules
					if (this.theme.defaultFont) {
						let bodySettings = '',
							fieldSettings = '';

						bodySettings += `background-color: ${bodyBg};`;
						bodySettings += `color: ${bodyFg};`;

						if (this.theme.defaultFont.family) {
							bodySettings += `font-family: ${this.theme.defaultFont.family};`;
							fieldSettings += `font-family: ${this.theme.defaultFont.family};`;
						}

						if (this.theme.defaultFont.size) {
							bodySettings += `font-size: ${this.theme.defaultFont.size};`;
						}

						this.insertRuleIntoStylesheet(styleSheet, `body { ${bodySettings} }`);
						this.insertRuleIntoStylesheet(styleSheet, `input, textarea, select, button { ${fieldSettings} }`);

						//override config list font
						this.insertRuleIntoStylesheet(styleSheet, `.cs-config-rootcell-title { ${fieldSettings} }`);

						//AG GRID font override
						this.insertRuleIntoStylesheet(styleSheet, `html body .ag-theme-material { ${fieldSettings} }`);

						//override config list font
						this.insertRuleIntoStylesheet(styleSheet, `.cs-config-rootcell-title { ${fieldSettings} }`);

						//AG GRID font override
						this.insertRuleIntoStylesheet(styleSheet, `html body .ag-theme-material { ${fieldSettings} }`);

						if (this.theme.defaultFont.lightweight) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-fw-light { font-weight: ${this.theme.defaultFont.lightweight}; }`);
						}

						if (this.theme.defaultFont.regularweight) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-fw-regular { font-weight: ${this.theme.defaultFont.regularweight}; }`);
						}

						if (this.theme.defaultFont.mediumweight) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-fw-medium { font-weight: ${this.theme.defaultFont.mediumweight}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-field-label { font-weight: ${this.theme.defaultFont.mediumweight}; }`);
						}

						if (this.theme.defaultFont.boldweight) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-fw-bold { font-weight: ${this.theme.defaultFont.boldweight}; }`);
						}
					}

					// create heading rules
					if (this.theme.headingFont) {
						let headingSettings = '';

						if (this.theme.headingFont.family) {
							headingSettings += `font-family: ${this.theme.headingFont.family};`;
						}

						if (this.theme.headingFont.weight) {
							headingSettings += `font-weight: ${this.theme.headingFont.weight};`;
						}
						this.insertRuleIntoStylesheet(styleSheet, `h1, h2, h3, h4, h5, h6 { ${headingSettings} }`);
					}

					// Accessibility rules
					if (this.theme && this.theme.colors !== undefined) {
						if (this.theme.colors.accent) {
							// Generic buttons
							this.insertRuleIntoStylesheet(styleSheet, `button.cs-accessible:focus::before { border-color : ${this.theme.colors.accent} !important; }`);
							
							this.insertRuleIntoStylesheet(styleSheet, `a.cs-button-icon.cs-accessible:focus::before { border-color : ${this.theme.colors.accent} !important; }`);
							// Generic buttons alt
							this.insertRuleIntoStylesheet(styleSheet, `button.cs-button.cs-bg-accent.cs-accessible:focus::before { border-color : ${this.theme.colors.secondary} !important; }`);
							// Home
							this.insertRuleIntoStylesheet(styleSheet, `.cs-home .cs-home-entity-selector .multiselect.cs-accessible .multiselect__content-wrapper { border-color : ${this.theme.colors.accent} !important; }`);
							// User avatar
							this.insertRuleIntoStylesheet(styleSheet, `.cs-header .cs-header--user-panel .cs-user-avatar button.cs-user-avatar-button:focus .cs-user-avatar-initials.cs-accessible { border-color : ${this.theme.colors.accent} !important; }`);
							// Export button
							this.insertRuleIntoStylesheet(styleSheet, `.cs-header .cs-export-button:focus.cs-accessible { outline-color: ${this.theme.colors.accent} !important; }`);
							// Search input
							this.insertRuleIntoStylesheet(styleSheet, `.cs-search .cs-search--expandable-input .cs-input-text.cs-accessible:focus { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-input-text--close.cs-accessible > svg:focus { outline-color : ${this.theme.colors.accent} !important }`);
							// Search List
							this.insertRuleIntoStylesheet(styleSheet, `.cs-search .cs-search-result-list li button.cs-accessible:focus { border-color : ${this.theme.colors.accent} !important; }`);
							// Skip Link
							this.insertRuleIntoStylesheet(styleSheet, `.cs-skip-link .cs-skip-link--link.cs-accessible:focus { outline-color : ${this.theme.colors.accent} !important; }`);
							// Navigation
							this.insertRuleIntoStylesheet(styleSheet, `.cs-navigation .cs-navigation-list .cs-navigation-list-item.cs-accessible:focus::before { border-color : ${this.theme.colors.accent} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-navigation .cs-navigation-list .cs-navigation-list-item.cs-accessible .cs-list-item--link:focus::before { border-color : ${this.theme.colors.accent} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-navigation-popup .cs-navigation-popup--list .cs-navigation-popup--list-item.cs-accessible > .cs-navigation-popup--list-item-link:focus::before { border-color : ${this.theme.colors.accent} !important; }`);
							// Sidebar
							this.insertRuleIntoStylesheet(styleSheet, `.cs-sidebar .cs-menu-root-homepage .cs-menu-root-homepage-link.cs-accessible a:focus { outline-color : ${this.theme.colors.accent} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-sidebar .cs-menu-dropdown .cs-menu-dropdown--selector.cs-accessible:focus::before { border-color : ${this.theme.colors.accent} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-navigation .cs-navigation-list.cs-accessible .cs-navigation-item:focus::before { border-color : ${this.theme.colors.accent} !important; }`);
							// Popdown list
							this.insertRuleIntoStylesheet(styleSheet, `.cs-list--item.cs-accessible:focus::before { border-color : ${this.theme.colors.accent} !important; }`);
							// Radio button input
							this.insertRuleIntoStylesheet(styleSheet, `.cs-radio-button input[type="radio"]:checked + label::before { border-color : ${this.theme.colors.accent} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-radio-button input[type="radio"]:checked + label::after { background-color : ${this.theme.colors.accent} !important; }`);
							// Field - switch field
							this.insertRuleIntoStylesheet(styleSheet, `.cs-field-switch-container .cs-field-switch--label.cs-accessible:focus .cs-field-switch--label__before::before { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - Alerts
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-alert:focus.cs-accessible { outline-color : ${this.theme.colors.accent} !important; }`);
							// Form - section header
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-section .cs-form-section-title-container.cs-form-section-title-container-focused.cs-accessible { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - section header alt
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-section .cs-form-section-title-container.cs-bg-accent.cs-form-section-title-container-focused.cs-accessible { border-color : ${this.theme.colors.secondary} !important; }`);
							// Form - select field
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-field-select.cs-accessible .multiselect__content-wrapper, .cs-multiselect-theme-colours.cs-accessible .multiselect__content-wrapper { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - version picker field
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-versionpicker .multiselect.cs-accessible .multiselect__content-wrapper { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - switch field
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-form-field-switch-container .cs-form-field-switch input:focus + .cs-accessible .cs-form-field-switch-label-before::before { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - repeater
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-form-field-repeater-row-focused.cs-accessible { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - attachment
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-attachment .cs-form-field-container .cs-accessible.cs-form-file:focus ~ .cs-form-file-label { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - wysiwyg editor
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-form-field.cs-accessible.cs-focused { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - date time picker
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-form-field.cs-form-field-date:focus { border-color : ${this.theme.colors.accent} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-date-time-picker .cs-date-time-picker-clear-date-button--wrapper:focus.cs-accessible .cs-date-time-picker-clear-date-button { border-color : ${this.theme.colors.accent} !important; }`);
							// Form - Froala - Wysiwyg editor
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-toolbar { border-color : ${this.theme.colors.accent}; !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-wrapper { border-color : ${this.theme.colors.accent}; !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .second-toolbar { border-color : ${this.theme.colors.accent}; !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-second-toolbar { border-color : ${this.theme.colors.accent}; !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-wysiwygeditor-container.cs-accessible .cs-froala-view:focus { border-color : ${this.theme.colors.accent}; !important }`);
							// Form - slider field
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-slider.cs-accessible .cs-slider-thumb--active { border-color : ${this.theme.colors.accent}; !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-slider.cs-accessible .cs-slider-track-fill--active { outline-color : ${this.theme.colors.accent}; !important }`);
							// Form - comments field
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-comment .cs-form-field-comments:focus.cs-accessible { outline-color : ${this.theme.colors.accent} !important }`);
							// Form - utility form view field
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-utility-form-view-field:focus.cs-accessible { outline-color : ${this.theme.colors.accent}; !important }`);
							// Form - User Select "Add" button
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-form-field-select-user-picker--add-button:focus.cs-accessible { border-color : ${this.theme.colors.accent} !important }`);
							// Form - QLC Button
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-field-actions .cs-form-field-actions li .cs-form-field-action:focus-within { outline-color : ${this.theme.colors.accent} !important }`);
							// Form - Sidebar Tab Button
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-form--sidebar .cs-form-sidebar-tabs .cs-form-sidebar-tab--button.cs-accessible:focus { outline-color : ${this.theme.colors.accent} !important }`);
							// Form - Sidebar Progress Container
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-form-sidebar-tab-content-progress .cs-form-progress-line.cs-accessible:focus { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form .cs-form-sidebar-tab-content-progress .cs-form-progress-line-section.cs-accessible:focus { border-color : ${this.theme.colors.accent} !important }`);
							// Form - Sidebar Comments Container
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-sidebar-container .cs-form-sidebar-tab-content:focus.cs-accessible { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-sidebar-tab-content-comments.cs-accessible .cs-qlc-selected-field-label:focus, .cs-form .cs-form-sidebar-tab-content-comments.cs-accessible .cs-qlc-rollup-item:focus { outline-color : ${this.theme.colors.accent} !important }`);
							// Accordions - accordion row header
							this.insertRuleIntoStylesheet(styleSheet, `.cs-accordion .cs-accordion--items .cs-accordion-item .cs-accordion-item--header__focused { border-color : ${this.theme.colors.accent} !important; }`);
							// Accordions - accordion row header title
							this.insertRuleIntoStylesheet(styleSheet, `.cs-accordion--items .cs-accordion-item .cs-accordion-item--header .cs-accordion-item--title .cs-accordion-item-title:focus.cs-accessible { outline-color : ${this.theme.colors.accent} !important; }`);
							// Accordions - accordion row header title input
							this.insertRuleIntoStylesheet(styleSheet, `.cs-accordion .cs-accordion--items .cs-accordion-item .cs-accordion-item--header .cs-accordion-item--title > input:focus.cs-accessible { border-color : ${this.theme.colors.accent} !important; }`);
							// Accordions - Froala - Wysiwyg editor
							this.insertRuleIntoStylesheet(styleSheet, `.cs-accordion .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-toolbar { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-accordion .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-wrapper { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-accordion .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .second-toolbar { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-accordion .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-second-toolbar { border-color : ${this.theme.colors.accent} !important }`);
							// Dashboard - card
							this.insertRuleIntoStylesheet(styleSheet, `.cs-reports-dashboard .cs-reports-dashboard--card-container .cs-reports-dashboard--card.cs-accessible:focus { border-color : ${this.theme.colors.accent} !important }`);
							// Dashboard - export menu
							this.insertRuleIntoStylesheet(styleSheet, `.cs-reports-dashboard .cs-report-export .cs-export-menu-item.cs-accessible:focus { border-color : ${this.theme.colors.accent} !important }`);
							// Dashboard - Froala - Field editor
							this.insertRuleIntoStylesheet(styleSheet, `.cs-reports-dashboard .cs-field-editor .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-toolbar { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-reports-dashboard .cs-field-editor .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-wrapper { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-reports-dashboard .cs-field-editor .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .second-toolbar { border-color : ${this.theme.colors.accent} !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-reports-dashboard .cs-field-editor .cs-wysiwygeditor-container.cs-accessible .fr-box.cs-focused .fr-second-toolbar { border-color : ${this.theme.colors.accent} !important }`);
							// AgGrid Context Menu Items
							this.insertRuleIntoStylesheet(styleSheet, `.cs-ag-grid .ag-menu .ag-menu-list .ag-menu-option.cs-accessible:focus { outline-color : ${this.theme.colors.accent}; !important }`);
							// AgGrid Header
							this.insertRuleIntoStylesheet(styleSheet, `.cs-ag-grid .ag-root .ag-header .ag-header-cell.ag-header-active:focus { border-color : ${this.theme.colors.accent}; !important }`);
							// Data Stream - CsGridSelectField
							this.insertRuleIntoStylesheet(styleSheet, `.cs-data-stream .cs-grid-select-options li.selected  { background-color : ${this.theme.colors.accent}; !important }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-data-stream .cs-grid-select-options li:focus.cs-accessible  { border-color : ${this.theme.colors.accent}; !important }`);
							// My CS 
							this.insertRuleIntoStylesheet(styleSheet, `.cs-my-corestream-container .cs-mycs-dashboard .cs-mycs-tiles-section .cs-mycs-multiselect .multiselect  { background-color : ${this.theme.colors.primary} !important; color : ${this.getContrastColour(this.theme.colors.primary, bodyFg, dark, light)} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-my-corestream-container .cs-mycs-dashboard .cs-mycs-tiles-section .cs-mycs-multiselect .multiselect .multiselect__input::placeholder  { color : ${this.getContrastColour(this.theme.colors.primary, bodyFg, dark, light)} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-my-corestream-container .cs-mycs-dashboard .cs-mycs-tiles-section .cs-mycs-multiselect .multiselect .multiselect__select  { color : ${this.getContrastColour(this.theme.colors.primary, bodyFg, dark, light)} !important; }`);
						}
					}

					// Filepond rules..
					this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--list .filepond--item .filepond--file, .cs-file-upload .filepond--root .filepond--list .filepond--item .filepond--file-status { color: ${bodyFg} !important; }`);
					if (this.theme && this.theme.colors) {
						// Filepond..
						if (this.theme.colors.secondary) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--drop-label label { border-color : ${this.theme.colors.secondary} !important; background-color: ${bodyBg} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--drop-label label:hover { border-color : ${this.theme.colors.secondary} !important; background-color: ${this.lightenColour(bodyBg, 10)} !important; }`);
						}
						if (this.theme.colors.field) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--list .filepond--item { box-shadow: 0 0 0 1px ${this.darkenColour(this.theme.colors.field, 20)} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--list .filepond--item .filepond--file-preview, .cs-file-upload .filepond--root .filepond--list .filepond--item .filepond--file-poster { background-color: ${this.theme.colors.field} !important; }`);
						}
						if (this.theme.colors.danger) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--item[data-filepond-item-state*='error'], .filepond--item[data-filepond-item-state*='invalid'] { box-shadow: 0 0 0 1px ${this.theme.colors.danger } !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--list .filepond--item.cs-filepond-file--file-error .filepond--file-preview, .cs-file-upload .filepond--root .filepond--list .filepond--item.cs-filepond-file--file-error .filepond--file-poster { background-color: ${this.lightenColour(this.theme.colors.danger, 99)} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--list .filepond--item.cs-filepond-file--file-error { box-shadow: 0 0 0 1px ${this.theme.colors.danger} !important; }`);
						}
						if (this.theme.colors.success) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-file-upload .filepond--root .filepond--item[data-filepond-item-state='processing-complete'] { box-shadow: 0 0 0 1px ${this.theme.colors.success } !important; }`);
						}
					}
					// end of filepond rules

					// create specific rules for forms
					if (this.theme.colors) {
						if (this.theme.colors.accent) {
							this.insertRuleIntoStylesheet(
								styleSheet,
								`.cs-form-field.cs-form-field-text:focus, .cs-form-field.cs-form-field-textarea:focus, .cs-form-field.cs-form-field-number:focus, .cs-form-field.cs-form-file:focus, .cs-form-field.cs-form-file:focus ~ .cs-form-file-label { border-bottom-color: ${this.theme.colors.accent} !important; }`
							);
							this.insertRuleIntoStylesheet(styleSheet, `
								.cs-form-field-select .multiselect__option--highlight, 
								.cs-form-field-select .multiselect__option--highlight:after, 
								.cs-multiselect-theme-colours .multiselect__option--highlight, 
								.cs-multiselect-theme-colours .multiselect__option--highlight:after 
								{ background-color : ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `
								.cs-form-field-select.multiselect--active .multiselect__tags, 
								.cs-multiselect-theme-colours.multiselect--active .multiselect__tags 
								{ border-bottom-color : ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `
								.cs-form-field-select .multiselect__tag-icon:hover, 
								.cs-multiselect-theme-colours .multiselect__tag-icon:hover 
								{ background-color: ${this.theme.colors.accent}; color : ${this.getContrastColour(this.theme.colors.accent, bodyFg, dark, light)} }`);
							// Question level comments
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-selected-question-level-comment-field { box-shadow: inset 0 0 4px ${this.theme.colors.accent} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-dual-comparison-difference-field { box-shadow: inset 0 0 4px ${this.theme.colors.warning} !important; }`);							
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-dual-comparison-difference-field-target { box-shadow: inset 0 0 4px ${this.theme.colors.warning} !important; }`);							
						}

						if (this.theme.colors.light) {
							this.insertRuleIntoStylesheet(
								styleSheet,
								`.cs-form-field.cs-form-field-text[disabled], .cs-form-field.cs-form-field-textarea[disabled], .cs-form-field.cs-form-file[disabled], .cs-form-field.cs-form-field-text[disabled]:focus, .cs-form-field.cs-form-field-textarea[disabled]:focus, .cs-form-field.cs-form-file[disabled]:focus { border-bottom-color: ${this.theme.colors.light} !important; }`
							);
						}

						if (this.theme.colors.dark) {
							this.insertRuleIntoStylesheet(styleSheet, `
								.cs-form-field-select .multiselect__tags, 
								.cs-multiselect-theme-colours .multiselect__tags
								{ border-bottom-color : ${this.theme.colors.dark}; }`);
						}

						if (this.theme.colors.danger) {
							this.insertRuleIntoStylesheet(
								styleSheet,
								`.cs-form-field-select .multiselect__option--selected.multiselect__option--highlight, 
								 .cs-form-field-select .multiselect__option--selected.multiselect__option--highlight:after,
								 .cs-multiselect-theme-colours .multiselect__option--selected.multiselect__option--highlight, 
								 .cs-multiselect-theme-colours .multiselect__option--selected.multiselect__option--highlight:after 
								{ background-color : ${
									this.theme.colors.danger
								}; color : ${this.getContrastColour(this.theme.colors.danger, bodyFg, dark, light)} }`
							);
						}

						if (this.theme.colors.primary) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-field-select .multiselect__tag, .cs-multiselect-theme-colours .multiselect__tag { background-color: ${this.theme.colors.primary}; color : ${this.getContrastColour(this.theme.colors.primary, bodyFg, dark, light)} }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-field-select .multiselect__tag-icon:after, .cs-multiselect-theme-colours .multiselect__tag-icon:after { color : ${this.getContrastColour(this.theme.colors.primary, bodyFg, dark, light)} }`);
						}

						if (this.theme.colors.field) {
							this.insertRuleIntoStylesheet(
								styleSheet,
								`.cs-form-field-container .cs-wysiwygeditor-container .cs-form-field .fr-wrapper { background-color: ${this.theme.colors.field}; color : ${this.getContrastColour(this.theme.colors.field, bodyFg, dark, light)} }`
							);
						}
					}

					let selectedStyle = bodyFg ? `color: ${bodyFg};` : '';

					if (this.theme.colors && this.theme.colors.light) {
						selectedStyle += `background-color: ${this.theme.colors.light};`;
					}

					if (this.theme.defaultFont && this.theme.defaultFont.mediumweight) {
						selectedStyle += `font-weight: ${this.theme.defaultFont.mediumweight};`;
					}

					if (styleSheet.length > 0) {
						this.insertRuleIntoStylesheet(styleSheet, `
							.cs-form-field-select .multiselect__option--selected, .cs-form-field-select .multiselect__option--selected:after,
							.cs-multiselect-theme-colours .multiselect__option--selected, .cs-multiselect-theme-colours .multiselect__option--selected:after
							{ ${selectedStyle} }`);
					}

					// Customs
					if (this.theme.colors) {
						if (this.theme.colors.accent) {
							// create specific rules for ag-grid
							// ag-icon-checkbox
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-checkbox-input-wrapper.ag-checked::after { color : ${this.theme.colors.accent}; }`);
							// ag-input
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-input-wrapper input:not([type]):focus { border-bottom-color : ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-input-wrapper input[type="text"]:focus { border-bottom-color : ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-input-wrapper input[type="number"]:focus { border-bottom-color : ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-input-wrapper input[type="tel"]:focus { border-bottom-color : ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-input-wrapper input[type="date"]:focus { border-bottom-color : ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-input-wrapper input[type="datetime-local"]:focus { border-bottom-color : ${this.theme.colors.accent}; }`);
							// ag-tab
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-tab.ag-tab-selected { background-color: ${this.theme.colors.accent}; border-bottom-color : ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-tab.ag-tab-selected .ag-icon { color : ${this.getContrastColour(this.theme.colors.accent, bodyFg, dark, light)}; }`);
							this.insertRuleIntoStylesheet(
								styleSheet,
								`.cs-component .ag-input-text-wrapper input:not([type]):focus, .ag-input-text-wrapper input[type="text"]:focus, .ag-input-text-wrapper input[type="tel"]:focus, .ag-input-text-wrapper input[type="date"]:focus, .ag-input-text-wrapper input[type="datetime-local"]:focus { border-bottom-color : ${this.theme.colors.accent}; }`
							);
							// ag-row : hover
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-root .ag-body-viewport .ag-row.ag-row-hover { background-color : ${this.theme.colors.accent}; color : ${this.getContrastColour(this.theme.colors.accent, bodyFg, dark, light)}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-root .ag-body-viewport .ag-row.ag-row-hover .ag-icon { color : ${this.getContrastColour(this.theme.colors.accent, bodyFg, dark, light)}; }`);
							// ag-cell : focus
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-root .ag-body-viewport .ag-cell.ag-cell-focus { border-color: ${this.theme.colors.accent}; }`);
							// ag-header
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-root .ag-header .ag-header-cell-filtered .ag-cell-label-container { border-bottom: 2px solid ${this.theme.colors.accent}; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-root .ag-header .ag-header-cell-sortable .ag-header-cell-sorted-asc { border-bottom: 2px solid ${this.theme.colors.accent} }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-component .ag-root .ag-header .ag-header-cell-sortable .ag-header-cell-sorted-desc { border-bottom: 2px solid ${this.theme.colors.accent} }`);
							// ag-menu
							this.insertRuleIntoStylesheet(
								styleSheet,
								`.cs-ag-grid .ag-popup .ag-menu .ag-menu-list .ag-menu-option.ag-menu-option-active { background-color : ${this.theme.colors.accent}; color : ${this.getContrastColour(this.theme.colors.accent, bodyFg, dark, light)}; }`
							);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-ag-grid .ag-popup .ag-menu .ag-menu-list .ag-menu-option.ag-menu-option-active .ag-icon { color : ${this.getContrastColour(this.theme.colors.accent, bodyFg, dark, light)}; }`);
							this.insertRuleIntoStylesheet(
								styleSheet,
								`.cs-ag-grid .ag-popup .ag-menu .ag-menu-list.cs-grid--popover-table .cs-grid--popover-table-row:not(.cs-grid--popover-table-header):hover { background-color : ${this.theme.colors.accent}; color : ${this.getContrastColour(
									this.theme.colors.accent,
									bodyFg,
									dark,
									light
								)}; }`
							);

							//Process mapping specific accent rules
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .csdr-icon:hover{stroke: ${this.theme.colors.accent}; fill: ${this.theme.colors.accent};}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .csdr-tool-selected{stroke: ${this.theme.colors.accent}; fill: ${this.theme.colors.accent};}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .csdr-element-highlight{border-color: ${this.theme.colors.accent};}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .cs-header-breadcrumbs:hover{color:${this.theme.colors.accent}}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .cs-header-breadcrumbs:hover path{fill:${this.theme.colors.accent}}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .csdr-shape-hover{stroke: ${this.theme.colors.accent} !important;}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .csdr-line-connector{stroke: ${this.theme.colors.accent} !important;}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .csdr-grid-horizontal{border-top-color: ${this.theme.colors.accent};}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .csdr-grid-vertical{border-left-color: ${this.theme.colors.accent};}`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .styler-list-item:hover{background-color: ${this.theme.colors.accent};`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-diagram-ui .cs-selected-styler-list-item{background-color: ${this.theme.colors.accent};`);


							//Gantt chart overrides
							this.insertRuleIntoStylesheet(styleSheet, `.e-date-wrapper .e-active{color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.e-calendar .e-content .e-today .e-day{border-color:${this.theme.colors.accent} !important; color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-gantt .e-splitter .e-split-bar-active{background-color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-gantt .e-splitter .e-split-bar-hover{background-color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-gantt .e-splitter .e-split-bar-active .e-resize-handler{color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-gantt .e-splitter .e-split-bar-hover .e-resize-handler{color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-gantt .e-input-group.e-control-wrapper.e-error:not(.e-float-icon-left){color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-gantt .e-grid .e-filtered::before{color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.e-input-group.e-control-wrapper:not(.e-float-icon-left):not(.e-float-input)::before{background: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.e-input-group.e-control-wrapper:not(.e-float-icon-left):not(.e-float-input)::after{background: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.e-input-group:not(.e-float-icon-left):not(.e-float-input)::before{background: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.e-input-group:not(.e-float-icon-left):not(.e-float-input)::after{background: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.e-dropdownbase .e-active{color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.e-primary{color: ${this.theme.colors.accent} !important;`);

							//Policy Manager v2 overrides 
							this.insertRuleIntoStylesheet(styleSheet, `.cs-policy-manager-body .cs-policy-body-scrollable-content-wrapper .cs-policy-body-marker{border-left-color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-policy-manager-body .cs-policy-body-quick-links-tab a:hover{background-color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-policy-manager-body .cs-policy-body-quick-links-tab a.active{background-color: ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-policy-attestations .cs-policy-attestations-title{border-bottom: solid 1px ${this.theme.colors.accent} !important;`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-policy-attestations .cs-policy-attestations-table tr:hover{color: ${this.theme.colors.accent} !important;`);

						}
					}

					//
					// advanced rules for granular control
					//
					if (this.theme.advancedcolors) {
						// Header rules
						if (this.theme.advancedcolors.headerbar || this.theme.advancedcolors.headerbarFG) {
							let headerBarRules = '';

							if (this.theme.advancedcolors.headerbar) {
								headerBarRules += `background-color: ${this.theme.advancedcolors.headerbar} !important;`;
							}

							if (this.theme.advancedcolors.headerbarFG) {
								headerBarRules += `color: ${this.theme.advancedcolors.headerbarFG} !important;`;
							}

							this.insertRuleIntoStylesheet(styleSheet, `.cs-header { ${headerBarRules} }`);
						}

						// Sidebar rules
						if (this.theme.advancedcolors.sidebar || this.theme.advancedcolors.sidebarFG) {
							let sidebarRules = '';

							if (this.theme.advancedcolors.sidebar) {
								sidebarRules += `background-color: ${this.theme.advancedcolors.sidebar} !important;`;
							}

							if (this.theme.advancedcolors.sidebarFG) {
								sidebarRules += `color: ${this.theme.advancedcolors.sidebarFG} !important;`;
							}

							this.insertRuleIntoStylesheet(styleSheet, `.cs-sidebar { ${sidebarRules} }`);
						}

						if (this.theme.advancedcolors.sidebarHover) {
							this.insertRuleIntoStylesheet(styleSheet, `.cs-navigation .cs-navigation--list .cs-navigation--list__item:hover { background-color : ${this.theme.advancedcolors.sidebarHover} !important; }`);
							this.insertRuleIntoStylesheet(styleSheet, `.cs-navigation-popup .cs-navigation-popup--list .cs-navigation-popup--list-item:hover { background-color : ${this.theme.advancedcolors.sidebarHover} !important; }`);
						}

						// Form rules
						if (this.theme.advancedcolors.formsectionheader || this.theme.advancedcolors.formsectionheaderFG) {
							let formsectionheaderRules = '';

							if (this.theme.advancedcolors.formsectionheader) {
								formsectionheaderRules += `background-color: ${this.theme.advancedcolors.formsectionheader} !important;`;
							}

							if (this.theme.advancedcolors.formsectionheaderFG) {
								formsectionheaderRules += `color: ${this.theme.advancedcolors.formsectionheaderFG} !important;`;
							}

							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-section-title-container { ${formsectionheaderRules} }`);
						}

						if (this.theme.advancedcolors.formsectionheaderOpen || this.theme.advancedcolors.formsectionheaderOpenFG) {
							let formsectionheaderOpenRules = '';

							if (this.theme.advancedcolors.formsectionheaderOpen) {
								formsectionheaderOpenRules += `background-color: ${this.theme.advancedcolors.formsectionheaderOpen} !important;`;
							}

							if (this.theme.advancedcolors.formsectionheaderOpenFG) {
								formsectionheaderOpenRules += `color: ${this.theme.advancedcolors.formsectionheaderOpenFG} !important;`;
							}

							this.insertRuleIntoStylesheet(styleSheet, `.cs-form-section-title-container.cs-form-section-title-container-open { ${formsectionheaderOpenRules} }`);
						}
					}

					//
					// add any custom rules
					//
					if (Array.isArray(this.theme.customRules)) {
						for (let c = 0, customRule; (customRule = this.theme.customRules[c]); c++) {
							if (customRule.rule) {
								this.insertRuleIntoStylesheet(styleSheet, customRule.rule);
							}
						}
					}
				}
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: true,
					errorCode: '000230'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * add a rule to a stylesheet, use this rather than directly using `insertRule` on the sheet object
		 * this will behave properly on IE11
		 *
		 * @param {object} stylesheet	The sheet object
		 * @param {string} rule 		The CSS rule
		 */
		insertRuleIntoStylesheet(stylesheet, rule) {
			try {
				stylesheet.insertRule(rule, stylesheet.cssRules.length > 0 ? stylesheet.cssRules.length : 0);
			} catch (error) {
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: true,
					errorCode: '000244'
				});
			}
		}
	}
};
